import styled from "@emotion/styled";
import { SidebarNav } from "../SidebarNav";
import { usePageTitle } from "src/Common/hooks/usePageTitle";
import { LAYOUT_SIDEBAR_WIDTH_PX } from "./layoutMetrics";

interface Props {
  children: React.ReactNode;
}

export const Layout = ({ children }: Props) => {
  usePageTitle();
  return (
    <Container>
      <SidebarNav />
      <Content>{children}</Content>
      <Version>v.1</Version>
    </Container>
  );
};

const Container = styled.div({
  // Pinned to the viewport, not `100vw`/`100vh`: those include the document's
  // scrollbar gutters, so any overflow made the shell wider and taller than the
  // window and the document kept its own scrollbars next to the content's.
  // `absolute` against the unpositioned body, not `fixed`: no stacking context,
  // so in-shell overlays keep their z-index against MUI's portals.
  position: "absolute",
  inset: 0,
  overflow: "hidden",
  display: "grid",
  // The content row takes the window's height, never its content's.
  gridTemplateRows: "minmax(0, 1fr)",
  // `minmax(0, 1fr)` (not the default `1fr`, whose implied minimum is
  // `min-content`) lets the content track shrink to its grid cell. Without
  // it, a wide child (e.g. the opener table) expands the track past its
  // share and blows the grid wider than 100vw, scrolling the whole page
  // horizontally and clipping the left edge.
  gridTemplateColumns: `${LAYOUT_SIDEBAR_WIDTH_PX}px minmax(0, 1fr)`,
});

// Absolute, not placed in a grid cell: an explicitly placed item claims its
// cell before auto-placement runs, which pushed the sidebar into the content
// column and the content into a second row under the sidebar.
const Version = styled.span({
  position: "absolute",
  left: 0,
  bottom: 0,
  width: LAYOUT_SIDEBAR_WIDTH_PX,
  textAlign: "center",
  zIndex: 1,
  pointerEvents: "none",
});

const Content = styled.div({
  minWidth: 0,
  overflowY: "auto",
});
